import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardSection,
  CardSectionDescription,
  CardSectionTitle,
  CardTitle,
} from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useHeadersManagerContext } from '../../explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { useGraphRef } from '../../hooks/useGraphRef';

interface Props {
  currentVariant: GraphQLTypes.VariantSettings__ExplorerPage_graph_variant;
  isFirstPublish: boolean;
  isCloudSupergraph: boolean;
}

export const ExplorerSettingsCard = ({
  currentVariant,
  isFirstPublish,
  isCloudSupergraph,
}: Props) => {
  const graphRef = useGraphRef();
  const location = useLocation();
  const { sharedHeaderDefinitions } = useHeadersManagerContext();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Explorer connection settings</CardTitle>
        <CardDescription>
          Explorer settings for {graphRef?.graphId}/{graphRef?.graphVariant}.
        </CardDescription>
        <Tooltip
          label="You must publish your first graph before accessing these settings."
          disabled={!isFirstPublish}
        >
          <Button
            variant="secondary"
            isDisabled={isFirstPublish}
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.connectionSettings,
            })}
            onClick={() => {
              /* eslint-disable @typescript-eslint/naming-convention */
              ampli.updateEndpointClicked({
                graph_ref: `${graphRef?.graphId}@${graphRef?.graphVariant}`,
                location: 'variant_settings',
              });
              /* eslint-enable @typescript-eslint/naming-convention */
            }}
          >
            Change
          </Button>
        </Tooltip>
      </CardHeader>
      <CardBody>
        <CardSection>
          <CardSectionTitle>Endpoint</CardSectionTitle>
          <CardSectionDescription>
            {currentVariant.url || 'No endpoint URL set.'}
          </CardSectionDescription>
        </CardSection>
        {!isCloudSupergraph && (
          <CardSection>
            <CardSectionTitle>Subscriptions</CardSectionTitle>
            <CardSectionDescription>
              {currentVariant.subscriptionUrl || 'No subscription URL set.'}
            </CardSectionDescription>
          </CardSection>
        )}
        <CardSection>
          <CardSectionTitle>Include cookies</CardSectionTitle>
          <CardSectionDescription>
            {currentVariant.sendCookies ? 'On' : 'Off'}
          </CardSectionDescription>
        </CardSection>
        <CardSection>
          <CardSectionTitle>Shared headers</CardSectionTitle>
          <CardSectionDescription>
            <>
              {sharedHeaderDefinitions.length &&
              sharedHeaderDefinitions[0]?.headerName !== '' ? (
                sharedHeaderDefinitions.map((header) => (
                  <div key={header.key}>
                    {header.headerName} : {header.value}
                  </div>
                ))
              ) : (
                <div>No shared headers have been set.</div>
              )}
            </>
          </CardSectionDescription>
        </CardSection>
      </CardBody>
    </Card>
  );
};
